import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FlyoutService } from '../flyout.service';
import {
  CampaignFilterRequest,
  CampaignTag,
  ComparisonOperatorType,
  ComponentFilter,
  ComponentType,
  ContentFilter,
  DateFilter,
  StatisticsFilter,
  StatusFilter,
  TypeFilter,
} from '../../campaign/campaign.model';
import { FlyoutOpeningData } from '../flyout-models';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CampaignFilterService } from '../../campaign/campaign-filter.service';
import { MatDialog } from '@angular/material/dialog';
import { SaveFilterDialogComponent } from './save-filter-dialog/save-filter-dialog.component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PageTitle } from '../../shared/utils/page-title';
import { Account } from 'app/back-office/account.model';

@Component({
  selector: 'app-flyout-campaign-filter',
  templateUrl: './flyout-campaign-filter.component.html',
  styleUrl: './flyout-campaign-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutCampaignFilterComponent implements AfterViewInit, OnInit {
  private _flyoutOpeningData: FlyoutOpeningData;
  campaignTags: Array<CampaignTag>;
  filterForm: FormGroup;
  comparisonFilterForm: FormGroup;
  settingInitialValues = false; // Add this line
  selectedTagIds: number[] = [];
  fromDateSet = false;
  toDateSet = false;
  searchControl: FormControl = new FormControl();
  accountControl: FormControl = new FormControl<number[]>([]);
  variationsControl: FormControl = new FormControl<number>(0);
  stepsControl: FormControl = new FormControl<number>(0);
  originalFilter: CampaignFilterRequest;
  isFilterChanged = false;
  public backOfficeAccounts: Array<Account>;
  public filteredBackofficeAccounts: Array<Account>;
  pageTitle: PageTitle;
  isBackoffice: boolean = false;

  typeFilter: ChipData[] = [
    { name: this.translationService.instant('generic.form'), statusKey: 'isForm', disabled: false },
    { name: this.translationService.instant('generic.promotion'), statusKey: 'isPromotion', disabled: false },
    { name: this.translationService.instant('generic.referral'), statusKey: 'isReferral', disabled: false },
    { name: this.translationService.instant('generic.survey'), statusKey: 'isSurvey', disabled: false },
    { name: this.translationService.instant('generic.nudge-tour'), statusKey: 'isProductTour', disabled: false },
    { name: this.translationService.instant('generic.quiz'), statusKey: 'isQuiz', disabled: false },
    { name: this.translationService.instant('generic.games'), statusKey: 'isGamification', disabled: false },
  ];

  statusFilter: ChipData[] = [
    { name: this.translationService.instant('campaign.status-Draft'), statusKey: 'isDraft' },
    { name: this.translationService.instant('campaign.status-Published-2'), statusKey: 'isPublished' },
    { name: this.translationService.instant('campaign.status-Unpublished'), statusKey: 'isUnPublished' },
    { name: this.translationService.instant('campaign.status-Scheduled'), statusKey: 'isScheduled' },
  ];

  contentFilter: ChipData[] = [
    { name: this.translationService.instant('campaign.filter.content.hasCustomStyles'), statusKey: 'hasCustomStyles' },
    { name: this.translationService.instant('campaign.filter.content.hasEventScripts'), statusKey: 'hasEventScripts' },
  ];
  components: ComponentChip[];
  comparisonOperators = [
    ComparisonOperatorType.greaterThanOrEqual,
    ComparisonOperatorType.lessThanOrEqual,
    ComparisonOperatorType.between,
  ];
  currentCampaignFilter: CampaignFilterRequest;

  @Input() set flyoutOpeningData(openingData: FlyoutOpeningData) {
    if (!openingData) {
      return;
    }
    this.backOfficeAccounts = openingData.backOfficeAccounts;
    this.filteredBackofficeAccounts = openingData.backOfficeAccounts;
    this.isBackoffice = openingData.isBackoffice;
    if (this.isBackoffice) {
      this.typeFilter.push({
        name: this.translationService.instant('campaign.ab-test.title'),
        statusKey: 'isAbTest',
        disabled: false,
      });
    }
    this.pageTitle = openingData.pageTitle;
    if (openingData.filterId === undefined) {
      this.checkPageAndDisableIfNeeded();
    }

    // Use campaignRequest if provided, otherwise get from service
    if (openingData.campaignRequest) {
      this.currentCampaignFilter = openingData.campaignRequest;
      this.campaignFilterService.prepareFilterForDisplay(this.currentCampaignFilter);
      this.originalFilter = JSON.parse(JSON.stringify(this.currentCampaignFilter));
    } else {
      this.currentCampaignFilter = this.campaignFilterService.getFilterForDisplay(
        openingData.filterId,
        openingData.pageTitle,
      );
    }
    this.originalFilter = this.campaignFilterService.unchangedFilter;
    if (this.currentCampaignFilter?.id === undefined || this.currentCampaignFilter?.id === null) {
      this.isFilterChanged =
        JSON.stringify(this.currentCampaignFilter.filterSettings) !==
        JSON.stringify(this.campaignFilterService.getPreparedEmptyFilter().filterSettings);
    } else {
      this.isFilterChanged =
        JSON.stringify(this.currentCampaignFilter.filterSettings) !==
        JSON.stringify(this.originalFilter.filterSettings);
    }
    this.campaignTags = openingData.campaignTags;
    this.campaignTags.sort((a, b) => b.usageCount - a.usageCount);
    this._flyoutOpeningData = openingData;
    this.searchControl.setValue(this.currentCampaignFilter.filterSettings.nameFilter?.substring);
    this.accountControl.setValue(
      this.currentCampaignFilter.filterSettings.accountFilter?.siteIds.map((x) => String(x)),
    );
    if (openingData.pageTitle === PageTitle.Backoffice) {
      this.variationsControl.setValue(this.currentCampaignFilter.filterSettings.componentFilter?.variations);
      this.stepsControl.setValue(this.currentCampaignFilter.filterSettings.componentFilter?.steps);
    } else {
      this.currentCampaignFilter.filterSettings.componentFilter = {} as ComponentFilter;
    }
    this.initializeComponents(this.currentCampaignFilter.filterSettings.componentFilter.componentType);
    this.fixDates();
    this.fixNumericComparisons();

    if (!openingData.isAdmin) {
      this.typeFilter = this.typeFilter.filter((type) => {
        return type.statusKey !== 'isQuiz' && type.statusKey !== 'isGamification';
      });
    }
  }

  private checkPageAndDisableIfNeeded() {
    if (this.pageTitle === PageTitle.Backoffice || this.pageTitle === PageTitle.Campaigns) {
      return;
    }
    this.typeFilter = [
      { name: this.translationService.instant('generic.form'), statusKey: 'isForm', disabled: true },
      { name: this.translationService.instant('generic.promotion'), statusKey: 'isPromotion', disabled: true },
      { name: this.translationService.instant('generic.referral'), statusKey: 'isReferral', disabled: true },
      { name: this.translationService.instant('generic.survey'), statusKey: 'isSurvey', disabled: true },
      { name: this.translationService.instant('generic.nudge-tour'), statusKey: 'isProductTour', disabled: true },
      { name: this.translationService.instant('generic.quiz'), statusKey: 'isQuiz', disabled: true },
      { name: this.translationService.instant('generic.games'), statusKey: 'isGamification', disabled: true },
    ];
  }

  get flyoutOpeningData(): FlyoutOpeningData {
    return this._flyoutOpeningData;
  }

  private fixDates() {
    // Initialize date filters if they exist in this._flyoutOpeningData.campaignFilter
    const dateFilter = this.currentCampaignFilter.filterSettings;
    if (dateFilter.lastPublished) {
      this.filterForm.get('filterType').setValue('lastPublished');
      this.filterForm.get('dateFilter').setValue({
        type: this.getDateFilterType(dateFilter.lastPublished),
        from: dateFilter?.lastPublished?.from ?? '',
        to: dateFilter?.lastPublished?.to ?? '',
      });
    } else if (dateFilter.lastUpdated) {
      this.filterForm.get('filterType').setValue('lastUpdated');
      this.filterForm.get('dateFilter').setValue({
        type: this.getDateFilterType(dateFilter.lastUpdated),
        from: dateFilter?.lastUpdated?.from ?? '',
        to: dateFilter?.lastUpdated?.to ?? '',
      });
    } else if (dateFilter.createdTime) {
      this.filterForm.get('filterType').setValue('createdTime');
      this.filterForm.get('dateFilter').setValue({
        type: this.getDateFilterType(dateFilter.createdTime),
        from: dateFilter?.createdTime?.from ?? '',
        to: dateFilter?.createdTime?.to ?? '',
      });
    } else {
      this.resetFilterToDefaultFilter();
    }
  }

  private fixNumericComparisons() {
    // Initialize numeric comparisons filters if they exist in the flyout data.
    const statisticsFilter = this.currentCampaignFilter.filterSettings.statisticsFilter;
    const componentFilter = this.currentCampaignFilter.filterSettings.componentFilter;
    if (componentFilter) {
      if (componentFilter.variationsComparisonType) {
        this.comparisonFilterForm.get('variationsFilter.comparison').setValue(componentFilter.variationsComparisonType);
        this.comparisonFilterForm.get('variationsFilter.value').setValue(componentFilter.variations);
        this.comparisonFilterForm.get('variationsFilter.valueTo').setValue(componentFilter.variationsTo);
      }
      if (componentFilter.stepsComparisonType) {
        this.comparisonFilterForm.get('stepsFilter.comparison').setValue(componentFilter.stepsComparisonType);
        this.comparisonFilterForm.get('stepsFilter.value').setValue(componentFilter.steps);
        this.comparisonFilterForm.get('stepsFilter.valueTo').setValue(componentFilter.stepsTo);
      }
    }
    if (statisticsFilter) {
      if (statisticsFilter.viewsComparisonType) {
        this.comparisonFilterForm.get('viewsFilter.comparison').setValue(statisticsFilter.viewsComparisonType);
        this.comparisonFilterForm.get('viewsFilter.value').setValue(statisticsFilter.views);
        this.comparisonFilterForm.get('viewsFilter.valueTo').setValue(statisticsFilter.viewsTo);
      }
      if (statisticsFilter.formResponsesComparisonType) {
        this.comparisonFilterForm
          .get('formResponsesFilter.comparison')
          .setValue(statisticsFilter.formResponsesComparisonType);
        this.comparisonFilterForm.get('formResponsesFilter.value').setValue(statisticsFilter.formResponses);
        this.comparisonFilterForm.get('formResponsesFilter.valueTo').setValue(statisticsFilter.formResponsesTo);
      }
      if (statisticsFilter.clickthroughsComparisonType) {
        this.comparisonFilterForm
          .get('clickthroughsFilter.comparison')
          .setValue(statisticsFilter.clickthroughsComparisonType);
        this.comparisonFilterForm.get('clickthroughsFilter.value').setValue(statisticsFilter.clickthroughs);
        this.comparisonFilterForm.get('clickthroughsFilter.valueTo').setValue(statisticsFilter.clickthroughsTo);
      }
    }
  }

  private getDateFilterType(dateFilter: DateFilter): string {
    if (dateFilter.from && dateFilter.to) {
      return 'between';
    } else if (dateFilter.from) {
      return 'after';
    } else if (dateFilter.to) {
      return 'before';
    } else {
      return 'never';
    }
  }

  constructor(
    private readonly flyoutService: FlyoutService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private campaignFilterService: CampaignFilterService,
    private translationService: TranslateService,
    public dialog: MatDialog,
  ) {
    // Initialize the date filter form group controls
    this.filterForm = this.fb.group({
      filterType: [null],
      dateFilter: this.fb.group({
        type: [''],
        from: [''], // Initialize 'from' form control here
        to: [''],
      }),
    });

    // Initialize the numeric comparison filter form group controls
    this.comparisonFilterForm = this.fb.group({
      variationsFilter: this.fb.group<ComparisonControl>({
        filterName: 'variations',
        comparison: null,
        value: 0,
        valueTo: 0,
      }),
      stepsFilter: this.fb.group<ComparisonControl>({
        filterName: 'steps',
        comparison: null,
        value: 0,
        valueTo: 0,
      }),
      viewsFilter: this.fb.group<ComparisonControl>({
        filterName: 'views',
        comparison: null,
        value: 0,
        valueTo: 0,
      }),
      formResponsesFilter: this.fb.group<ComparisonControl>({
        filterName: 'formResponses',
        comparison: null,
        value: 0,
        valueTo: 0,
      }),
      clickthroughsFilter: this.fb.group<ComparisonControl>({
        filterName: 'clickthroughs',
        comparison: null,
        value: 0,
        valueTo: 0,
      }),
    });
  }

  ngOnInit(): void {
    this.comparisonFilterForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((values) => {
      this.currentCampaignFilter.filterSettings.statisticsFilter = {} as StatisticsFilter;
      const variationsFilter = values.variationsFilter as ComparisonControl;
      const stepsFilter = values.stepsFilter as ComparisonControl;
      const viewsFilter = values.viewsFilter as ComparisonControl;
      const formResponsesFilter = values.formResponsesFilter as ComparisonControl;
      const clickthroughsFilter = values.clickthroughsFilter as ComparisonControl;
      this.addNumericComparison(variationsFilter);
      this.addNumericComparison(stepsFilter);
      this.addNumericComparison(viewsFilter);
      this.addNumericComparison(formResponsesFilter);
      this.addNumericComparison(clickthroughsFilter);
      if (this.shouldSendFilterRequestComparison()) {
        this.flyoutService.sendFilterCampaignFlyout({
          campaignFilterRequest: this.currentCampaignFilter,
        });
        this.checkIfFilterChanged();
      }
    });
    this.filterForm.valueChanges.pipe().subscribe((values) => {
      const filterType = values.filterType;
      const dateFilter = values.dateFilter;

      // Set all date filters to undefined
      this.currentCampaignFilter.filterSettings.lastUpdated = undefined;
      this.currentCampaignFilter.filterSettings.createdTime = undefined;
      this.currentCampaignFilter.filterSettings.lastPublished = undefined;

      // If a filter type is selected, initialize the corresponding date filter
      if (filterType) {
        if (!this.currentCampaignFilter.filterSettings[filterType]) {
          this.currentCampaignFilter.filterSettings[filterType] = { from: null, to: null };
        }
        // Update the corresponding date filter when the date filter changes
        if (filterType === 'createdTime' || filterType === 'lastUpdated' || filterType === 'lastPublished') {
          this.currentCampaignFilter.filterSettings[filterType] = {
            from: dateFilter.from === '' ? undefined : dateFilter.from,
            to: dateFilter.to === '' ? undefined : dateFilter.to,
          };
        } else {
          this.currentCampaignFilter.filterSettings[filterType] = undefined;
        }

        if (this.shouldSendFilterRequestDate()) {
          this.flyoutService.sendFilterCampaignFlyout({
            campaignFilterRequest: this.currentCampaignFilter,
          });
          this.checkIfFilterChanged();
        }
      } else {
        this.currentCampaignFilter.filterSettings[filterType] = undefined;
        this.flyoutService.sendFilterCampaignFlyout({
          campaignFilterRequest: this.currentCampaignFilter,
        });
        this.checkIfFilterChanged();
      }
    });

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300), // Wait for 300ms pause in events
        distinctUntilChanged(), // Only emit if value is different from previous value
      )
      .subscribe((searchTerm: string) => {
        // Trigger your search action here
        this.triggerSearchByTerm(searchTerm);
        //this.checkIfFilterChanged();
      });

    this.accountControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(), // Only emit if value is different from previous value
      )
      .subscribe((selectedAccounts: number[]) => {
        if (this.currentCampaignFilter.filterSettings.accountFilter === undefined) {
          this.currentCampaignFilter.filterSettings.accountFilter = {};
        }

        if (selectedAccounts && selectedAccounts.length > 0) {
          this.currentCampaignFilter.filterSettings.accountFilter.siteIds = selectedAccounts;
        } else {
          // If no accounts selected, remove the filter
          this.currentCampaignFilter.filterSettings.accountFilter = undefined;
        }

        this.flyoutService.sendFilterCampaignFlyout({
          campaignFilterRequest: this.currentCampaignFilter,
        });
        this.checkIfFilterChanged();
      });
    this.variationsControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((variations: number) => {
        if (variations === this.currentCampaignFilter.filterSettings.componentFilter?.variations) return;
        this.currentCampaignFilter.filterSettings.componentFilter.variations = variations;
        this.flyoutService.sendFilterCampaignFlyout({
          campaignFilterRequest: this.currentCampaignFilter,
        });
        this.checkIfFilterChanged();
      });
    this.stepsControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((steps: number) => {
      if (steps === this.currentCampaignFilter.filterSettings.componentFilter?.steps) return;
      this.currentCampaignFilter.filterSettings.componentFilter.steps = steps;
      this.flyoutService.sendFilterCampaignFlyout({
        campaignFilterRequest: this.currentCampaignFilter,
      });
      this.checkIfFilterChanged();
    });
  }

  isSelected(number: number): boolean {
    return this.filterForm.get('filterType')?.value?.includes(number) ?? false;
  }

  private addNumericComparison(filter: ComparisonControl) {
    if (!filter) return;
    // Receives a filter form control group and updates the corresponding campaign filter setting.
    switch (filter.comparison) {
      case 'greaterThanOrEqual':
      case 'lessThanOrEqual':
        if (filter.filterName === 'steps' || filter.filterName === 'variations') {
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName + 'ComparisonType'] =
            ComparisonOperatorType[filter.comparison];
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName] = filter.value;
          break;
        } else {
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName + 'ComparisonType'] =
            ComparisonOperatorType[filter.comparison];
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName] = filter.value;
          break;
        }
      case 'between':
        if (filter.filterName === 'steps' || filter.filterName === 'variations') {
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName + 'ComparisonType'] =
            ComparisonOperatorType[filter.comparison];
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName] = filter.value;
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName + 'To'] = filter.valueTo;
          break;
        } else {
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName + 'ComparisonType'] =
            ComparisonOperatorType[filter.comparison];
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName] = filter.value;
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName + 'To'] = filter.valueTo;
          break;
        }
      case '':
        if (filter.filterName === 'steps' || filter.filterName === 'variations') {
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName + 'ComparisonType'] = null;
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName] = 0;
          this.currentCampaignFilter.filterSettings.componentFilter[filter.filterName] = 0;
        } else {
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName + 'ComparisonType'] = null;
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName] = 0;
          this.currentCampaignFilter.filterSettings.statisticsFilter[filter.filterName + 'To'] = 0;
        }
        break;
    }
  }

  private triggerSearchByTerm(searchTerm: string) {
    if (this.currentCampaignFilter.filterSettings.nameFilter == undefined) {
      this.currentCampaignFilter.filterSettings.nameFilter = {};
    }

    if (searchTerm === this.currentCampaignFilter.filterSettings.nameFilter.substring) {
      return;
    }
    if (searchTerm === '') {
      this.currentCampaignFilter.filterSettings.nameFilter = undefined;
      this.flyoutService.sendFilterCampaignFlyout({
        campaignFilterRequest: this.currentCampaignFilter,
      });
      this.checkIfFilterChanged();
      return;
    }
    this.currentCampaignFilter.filterSettings.nameFilter.substring = searchTerm;
    this.flyoutService.sendFilterCampaignFlyout({
      campaignFilterRequest: this.currentCampaignFilter,
    });
    this.checkIfFilterChanged();
  }

  ngAfterViewInit(): void {
    this.settingInitialValues = true; // Set the flag to true before setting initial values

    this.typeFilter.forEach((item) => {
      item.selected = this.currentCampaignFilter?.filterSettings?.typeFilter[item.statusKey];
    });

    this.statusFilter.forEach((item) => {
      item.selected = this.currentCampaignFilter?.filterSettings?.statusFilter[item.statusKey];
    });

    this.contentFilter.forEach((item) => {
      item.selected = this.currentCampaignFilter?.filterSettings?.componentFilter[item.statusKey];
    });

    this.campaignTags.forEach((tag) => {
      tag.selected = this.currentCampaignFilter?.filterSettings?.tags?.includes(tag.id);
    });

    this.campaignTags = this.campaignTags?.filter((tag) => tag.usageCount > 0 || tag.selected);
    this.cdr.detectChanges();
    this.settingInitialValues = false; // Set the flag back to false after setting initial values
  }

  private shouldSendFilterRequestDate(): boolean {
    const filterType = this.filterForm.get('filterType').value;
    const dateFilter = this.filterForm.get('dateFilter').value;
    let myReturn = false;

    if (filterType === 'createdTime' || filterType === 'lastUpdated' || filterType === 'lastPublished') {
      // Check if the 'from' date and or 'to' date is set

      if (dateFilter.from) {
        this.fromDateSet = true;
        myReturn = true;
      } else if (this.fromDateSet) {
        myReturn = true;
        this.fromDateSet = false;
      }
      if (dateFilter.to) {
        this.toDateSet = true;
        myReturn = true;
      } else if (this.toDateSet) {
        this.toDateSet = false;
        myReturn = true;
      }
    }
    return myReturn;
  }

  private shouldSendFilterRequestComparison(): boolean {
    const variations = this.validateComparison('variations');
    const steps = this.validateComparison('steps');
    const views = this.validateComparison('views');
    const formResponses = this.validateComparison('formResponses');
    const clickthroughs = this.validateComparison('clickthroughs');
    return variations || steps || views || formResponses || clickthroughs;
  }

  private validateComparison(filterName: string): boolean {
    const componentFilter = this.currentCampaignFilter.filterSettings.componentFilter;
    if (filterName === 'variations' || filterName === 'steps') {
      if (componentFilter[filterName + 'ComparisonType'] !== ComparisonOperatorType.between) {
        return true;
      } else if (componentFilter[filterName + 'ComparisonType'] === ComparisonOperatorType.between) {
        return true;
      } else {
        componentFilter[filterName + 'ComparisonType'] = null;
        componentFilter[filterName] = 0;
        componentFilter[filterName + 'To'] = 0;
      }
      return false;
    }
    const statisticsFilter = this.currentCampaignFilter.filterSettings.statisticsFilter;
    if (!statisticsFilter) return false;
    if (statisticsFilter[filterName + 'ComparisonType'] !== ComparisonOperatorType.between) {
      return true;
    } else if (statisticsFilter[filterName + 'ComparisonType'] === ComparisonOperatorType.between) {
      return true;
    } else {
      statisticsFilter[filterName + 'ComparisonType'] = null;
      statisticsFilter[filterName] = 0;
      statisticsFilter[filterName + 'To'] = 0;
    }
    return false;
  }

  onSelectionChangeTags(event, tag: CampaignTag) {
    if (event?.source?.value == null) {
      return;
    }

    tag.selected = event.source.selected;

    const selectedTag = event.source.value;
    const selectedTagId = selectedTag.id; // Access the id of the selected tag

    this.selectedTagIds.includes(selectedTagId)
      ? (this.selectedTagIds = this.selectedTagIds.filter((tagId) => tagId !== selectedTagId))
      : this.selectedTagIds.push(selectedTagId);

    if (!this.settingInitialValues) {
      this.currentCampaignFilter.filterSettings['tags'] = this.selectedTagIds;
      this.flyoutService.sendFilterCampaignFlyout({
        campaignFilterRequest: this.currentCampaignFilter,
      });
      this.checkIfFilterChanged();
    }

    if (this.selectedTagIds.length === 0) {
      this.currentCampaignFilter.filterSettings['tags'] = undefined;
    }
  }

  onSelectionChange(event, filterType: string) {
    if (event?.source?.value == null) {
      return;
    }

    let filterArray;
    let filterData;

    if (filterType === 'typeFilter') {
      filterArray = this.typeFilter;
      filterData = this.currentCampaignFilter.filterSettings.typeFilter;
    } else if (filterType === 'statusFilter') {
      filterArray = this.statusFilter;
      filterData = this.currentCampaignFilter.filterSettings.statusFilter;
    } else if (filterType === 'contentFilter') {
      filterArray = this.contentFilter;
      filterData = this.currentCampaignFilter.filterSettings.componentFilter;
    }

    this.updateFilter(event, filterArray, filterData);
    if (!this.settingInitialValues) {
      this.flyoutService.sendFilterCampaignFlyout({
        campaignFilterRequest: this.currentCampaignFilter,
      });
    }
  }

  onComponentChange(event, componentType: ComponentType) {
    if (event?.source?.value == null) {
      return;
    }

    if (this.currentCampaignFilter.filterSettings.componentFilter.componentType == componentType) {
      this.currentCampaignFilter.filterSettings.componentFilter.componentType = null;
    } else {
      this.currentCampaignFilter.filterSettings.componentFilter.componentType = componentType;
    }
    this.checkIfFilterChanged();
    this.flyoutService.sendFilterCampaignFlyout({
      campaignFilterRequest: this.currentCampaignFilter,
    });
  }

  updateFilter(event, filterArray: ChipData[], filterData: CombinedFilter) {
    const item = filterArray.find((item) => item.name === event.source.value);
    if (item) {
      item.selected = event.source.selected;
      filterData[item.statusKey] = event.source.selected;
      if (item.statusKey === 'isScheduled') {
        filterData.isScheduledByDate = event.source.selected;
        filterData.isScheduledByDaysAndHours = event.source.selected;
      }
    }
    this.checkIfFilterChanged();
  }

  saveFilter() {
    this.openDialog();
  }

  deleteFilter() {
    if (this.currentCampaignFilter.id != undefined) {
      this.campaignFilterService.deleteSavedCampaignFilter(this.currentCampaignFilter.id);
      this.cdr.detectChanges();
      this.flyoutService.close();
    }
  }

  openDialog() {
    const saveFilterDialogRef = this.dialog.open(SaveFilterDialogComponent, {
      data: this.currentCampaignFilter,
    });

    saveFilterDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.currentCampaignFilter.id != undefined) {
          this.currentCampaignFilter.hasUnsavedChanges = false;
          this.campaignFilterService.updateSavedCampaignFilter(this.currentCampaignFilter);
        } else {
          this.campaignFilterService.createNewCampaignFilter(this.currentCampaignFilter);
          this.currentCampaignFilter.hasUnsavedChanges = false;
        }
        this.flyoutService.close();
      }
    });
  }

  clearFromDate(fromInput: HTMLInputElement) {
    this.filterForm.get('dateFilter').get('from').setValue('');
    fromInput.blur();
  }

  clearToDate() {
    this.filterForm.get('dateFilter').get('to').setValue('');
  }

  cancelFlyout() {
    this.flyoutService.close();
  }

  protected readonly close = close;

  resettingFilters = false;

  resetFilter() {
    if (this.currentCampaignFilter.id == undefined) {
      this.campaignFilterService.resetLocalFilter();
    }

    this.campaignFilterService.prepareFilterForDisplay(this.originalFilter);
    this.currentCampaignFilter = JSON.parse(JSON.stringify(this.originalFilter));
    this.ngAfterViewInit();
    this.cdr.detectChanges();
    this.searchControl.setValue(this.currentCampaignFilter.filterSettings.nameFilter?.substring);
    this.accountControl.setValue(this.currentCampaignFilter.filterSettings.accountFilter?.siteIds);
    if (this.currentCampaignFilter.filterSettings.nameFilter?.substring === '') {
      this.currentCampaignFilter.filterSettings.nameFilter = undefined;
    }

    this.flyoutService.sendFilterCampaignFlyout({
      campaignFilterRequest: this.currentCampaignFilter,
    });
    this.fixDates();
    this.isFilterChanged = false;
    this.campaignFilterService.resetUnsavedFilter();

    if (this.currentCampaignFilter.id != undefined) {
      this.currentCampaignFilter = this.campaignFilterService.getFilterForDisplay(
        this.currentCampaignFilter.id,
        this.pageTitle,
      );
      this.originalFilter = this.campaignFilterService.unchangedFilter;
    } else {
      this.currentCampaignFilter = this.campaignFilterService.getFilterForDisplay(undefined, this.pageTitle);
      this.originalFilter = this.campaignFilterService.unchangedFilter;
    }

    this.flyoutService.close();
  }

  resetFilterToDefaultFilter() {
    this.filterForm = this.fb.group({
      filterType: [null],
      dateFilter: this.fb.group({
        type: [''],
        from: [''], // Initialize 'from' form control here
        to: [''],
      }),
    });
  }

  checkIfFilterChanged() {
    if (this.currentCampaignFilter.id == undefined) {
      // const emptyFilter = this.campaignFilterService.getLocalFilter();
      this.cdr.detectChanges();
      this.isFilterChanged = true;
      return;
    }
    const currentFilter = this.currentCampaignFilter.filterSettings;
    const originalFilter = this.originalFilter.filterSettings;
    this.isFilterChanged = JSON.stringify(currentFilter) !== JSON.stringify(originalFilter);
    this.currentCampaignFilter.hasUnsavedChanges = this.isFilterChanged;
    this.cdr.detectChanges();

    if (this.isFilterChanged) {
      this.campaignFilterService.setIdWithChanges(this.currentCampaignFilter.id);
    } else {
      this.campaignFilterService.setIdWithChanges(undefined);
    }
  }

  initializeComponents(componentType?: ComponentType) {
    this.components = [
      {
        name: this.translationService.instant('campaign.widget.component.spin-the-wheel.title'),
        value: ComponentType.spinTheWheel,
        selected: componentType == ComponentType.spinTheWheel,
      },
      {
        name: this.translationService.instant('campaign.widget.component.deadline.title'),
        value: ComponentType.deadline,
        selected: componentType == ComponentType.deadline,
      },
      {
        name: this.translationService.instant('campaign.widget.component.memory.title'),
        value: ComponentType.memory,
        selected: componentType === ComponentType.memory,
      },
      {
        name: this.translationService.instant('campaign.widget.component.discountCode.code'),
        value: ComponentType.couponCode,
        selected: componentType == ComponentType.couponCode,
      },
      {
        name: this.translationService.instant('campaign.widget.component.discountCode.single-use'),
        value: ComponentType.uniqueCouponCode,
        selected: componentType == ComponentType.uniqueCouponCode,
      },
      {
        name: this.translationService.instant('campaign.widget.component.text.floating-title'),
        value: ComponentType.sticker,
        selected: componentType == ComponentType.sticker,
      },
      {
        name: this.translationService.instant('campaign.widget.component.input-text-phone.title'),
        value: ComponentType.basicPhoneNumber,
        selected: componentType == ComponentType.basicPhoneNumber,
      },
      {
        name: this.translationService.instant('campaign.widget.component.radioButtons.title-checkboxes'),
        value: ComponentType.checkboxList,
        selected: componentType == ComponentType.checkboxList,
      },
      {
        name: this.translationService.instant('campaign.widget.component.buttonChoices.filter-title'),
        value: ComponentType.buttonChoices,
        selected: componentType == ComponentType.buttonChoices,
      },
      {
        name: this.translationService.instant('campaign.widget.component.share-campaign.title'),
        value: ComponentType.shareButton,
        selected: componentType == ComponentType.shareButton,
      },
    ] as ComponentChip[];
  }
}

type CombinedFilter = TypeFilter & StatusFilter & ContentFilter;

export interface ChipData {
  name: string;
  selected?: boolean;
  statusKey?: keyof CombinedFilter;
  disabled?: boolean;
}

export interface ComponentChip {
  name: string;
  selected?: boolean;
  value: ComponentType;
  disabled?: boolean;
}

export interface ComparisonControl {
  filterName: string;
  comparison: string | null;
  value: number;
  valueTo: number;
}
